import React, { useState, useEffect, useMemo, useRef } from "react";
import Papa from "papaparse";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfYear,
  endOfYear,
  addMinutes,
} from "date-fns";
import { fr, nl, enUS } from "date-fns/locale";
import { AlertCircle, Filter, ChevronDown, X, RefreshCw } from "lucide-react";
import { supabase } from "../lib/supabase";
import { toast } from "react-hot-toast";
import { useTranslation } from 'react-i18next';

// Aires claires empilées visuellement : total (partagé + réseau) derrière, partagé devant.
// Le haut de la courbe correspond donc toujours au total.
const QH = {
  totalFill: "#D6E4EA",
  totalStroke: "#2F6F8F",
  sharedFill: "#FDE6CB",
  sharedStroke: "#F59B42",
  ink: "#0C1C2C",
  teal: "#00516F",
  muted: "#51626E",
  border: "#C9DFE5",
  grid: "#E3ECEF",
};
const ALL_TYPES = ["Consommation Partagée", "Consommation Réseau", "Injection Partagée", "Injection Réseau"];

const CACHE_VERSION = "v2";
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 heure

function getCacheKey(url) {
  return `csv_cache_${CACHE_VERSION}_${btoa(url).slice(0, 40)}`;
}

function getCachedCsv(url) {
  try {
    const key = getCacheKey(url);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { rows, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return rows;
  } catch {
    return null;
  }
}

function setCachedCsv(url, rows) {
  try {
    const key = getCacheKey(url);
    localStorage.setItem(key, JSON.stringify({ rows, ts: Date.now() }));
  } catch {
    // localStorage plein — on ignore silencieusement
  }
}

function parseDate(dateStr) {
  if (!dateStr) return null;
  try {
    const parts = dateStr.trim().split(" ");
    if (!parts[0]) return null;
    const dateParts = parts[0].split("/");
    if (dateParts.length !== 3) return null;
    const [day, month, year] = dateParts;
    if (!day || !month || !year) return null;
    const time = parts[1] || "00:00";
    const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${time}`;
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return null;
    return date;
  } catch {
    return null;
  }
}

function parseCsvUrl(url, eansSet) {
  return new Promise((resolve, reject) => {
    const cached = getCachedCsv(url);
    if (cached) {
      resolve(cached.filter(r => eansSet.has(r.ean)));
      return;
    }

    Papa.parse(url, {
      download: true,
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const allRows = [];
        for (const r of result.data) {
          if (!r["Date Début"] || !r["EAN"] || !r["Type de volume"]) continue;
          const ean = r["EAN"].trim();
          const date = parseDate(r["Date Début"]);
          if (!date) continue;
          const volumeStr = r["Volume (kWh)"];
          const volume = parseFloat(volumeStr ? volumeStr.toString().replace(',', '.') : '0') || 0;
          const type = r["Type de volume"].trim();
          allRows.push({ ts: date.getTime(), ean, type, volume });
        }
        setCachedCsv(url, allRows);
        resolve(allRows.filter(r => eansSet.has(r.ean)));
      },
      error: (err) => reject(err),
    });
  });
}

export default function MemberEanChart({ participantEan, participantType = 'consumer', groupEans = [], autoRefresh = false, periodStart, periodEnd }) {
  const { t, i18n } = useTranslation();

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'nl': return nl;
      case 'en': return enUS;
      default: return fr;
    }
  };

  const [data, setData] = useState([]);
  const [availableDataTypes, setAvailableDataTypes] = useState(new Set());
  const [granularity, setGranularity] = useState("day");
  const [selectedDate, setSelectedDate] = useState(() => {
    if (periodStart) return new Date(periodStart);
    const d = new Date();
    d.setMonth(d.getMonth() - 2);
    return d;
  });
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ done: 0, total: 0 });
  const [qhMode, setQhMode] = useState(participantType === 'producer' ? 'inj' : 'conso');
  const [visibleAreas, setVisibleAreas] = useState({ total: true, shared: true });

  useEffect(() => {
    setQhMode(participantType === 'producer' ? 'inj' : 'conso');
  }, [participantType]);

  const hasInjectionData = availableDataTypes.has("Injection Partagée") || availableDataTypes.has("Injection Réseau");
  const hasConsoData = availableDataTypes.has("Consommation Partagée") || availableDataTypes.has("Consommation Réseau");
  // Bascule Consommation / Injection : prosumers et groupes mixtes (données des deux sens)
  const showModeSwitch = participantType === 'prosumer' || (hasInjectionData && hasConsoData);
  const effectiveMode = showModeSwitch
    ? qhMode
    : (hasInjectionData && !hasConsoData) ? 'inj' : (hasConsoData && !hasInjectionData) ? 'conso' : qhMode;
  const modePrefix = effectiveMode === 'inj' ? 'Injection' : 'Consommation';

  const [participants, setParticipants] = useState([]);
  const [selectedParticipantsFilter, setSelectedParticipantsFilter] = useState(["Tous"]);
  const [showParticipantFilter, setShowParticipantFilter] = useState(false);
  const [availableDataPeriod, setAvailableDataPeriod] = useState(null);

  const eanToNameRef = useRef({});
  const filterRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowParticipantFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (groupEans.length === 0) return;
    supabase
      .from('participants')
      .select('ean_code, name')
      .in('ean_code', groupEans)
      .then(({ data: participantData, error }) => {
        if (!error && participantData) {
          const mapping = {};
          participantData.forEach(p => {
            if (p.ean_code && p.name) mapping[p.ean_code] = p.name;
          });
          eanToNameRef.current = mapping;
          const names = participantData.map(p => p.name).filter(Boolean);
          setParticipants(["Tous", ...names.sort()]);
        }
      });
  }, [groupEans]);

  const initialLoadDone = useRef(false);

  // Calcule la plage de mois à charger depuis les props periodStart/periodEnd
  const getMonthRange = () => {
    const startDate = periodStart ? new Date(periodStart) : new Date();
    const endDate = periodEnd ? new Date(periodEnd) : new Date();
    return {
      startY: startDate.getFullYear(),
      startM: startDate.getMonth() + 1,
      endY: endDate.getFullYear(),
      endM: endDate.getMonth() + 1,
    };
  };

  useEffect(() => {
    if (!participantEan) return;
    const isFirstLoad = !initialLoadDone.current;
    initialLoadDone.current = true;
    loadData(isFirstLoad && autoRefresh);
  }, [participantEan, participantType, groupEans.length, periodStart, periodEnd]);

  const loadData = async (forceRefresh = false) => {
    if (!participantEan) return;

    if (forceRefresh) {
      try {
        const { data: csvLinks } = await supabase
          .from("monthly_csv_links")
          .select("csv_url");
        if (csvLinks) {
          const uniqueUrls = [...new Set(csvLinks.map(l => l.csv_url).filter(Boolean))];
          uniqueUrls.forEach(url => {
            try { localStorage.removeItem(getCacheKey(url)); } catch {}
          });
        }
      } catch {}
    }

    setLoading(true);
    setLoadingProgress({ done: 0, total: 0 });

    const isProducer = participantType === 'producer';
    const eansToLoad = isProducer && groupEans.length > 0 ? groupEans : [participantEan];
    const eansSet = new Set(eansToLoad);

    const { startY, startM, endY, endM } = getMonthRange();

    try {
      let query = supabase
        .from("monthly_csv_links")
        .select("*")
        .order("year", { ascending: true })
        .order("month_number", { ascending: true });

      if (startY === endY) {
        query = query
          .eq("year", startY)
          .gte("month_number", startM)
          .lte("month_number", endM);
      } else if (endY - startY === 1) {
        query = query.or(
          `and(year.eq.${startY},month_number.gte.${startM}),and(year.eq.${endY},month_number.lte.${endM})`
        );
      } else {
        const middleYears = [];
        for (let y = startY + 1; y < endY; y++) middleYears.push(y);
        const orParts = [
          `and(year.eq.${startY},month_number.gte.${startM})`,
          `and(year.eq.${endY},month_number.lte.${endM})`,
          ...middleYears.map(y => `year.eq.${y}`),
        ];
        query = query.or(orParts.join(","));
      }

      const { data: csvLinks, error } = await query;

      if (error) throw error;

      if (!csvLinks || csvLinks.length === 0) {
        toast.error("Aucun fichier CSV disponible pour cette période");
        setLoading(false);
        return;
      }

      const urls = [...new Set(csvLinks.map(link => link.csv_url).filter(Boolean))];
      setLoadingProgress({ done: 0, total: urls.length });

      // Chargement parallèle avec Promise.allSettled
      let done = 0;
      const results = await Promise.allSettled(
        urls.map(url =>
          parseCsvUrl(url, eansSet).then(rows => {
            done++;
            setLoadingProgress({ done, total: urls.length });
            return rows;
          })
        )
      );

      const allData = [];
      for (const result of results) {
        if (result.status === 'fulfilled') {
          for (const row of result.value) allData.push(row);
        }
      }

      const typesPresent = new Set(allData.map(d => d.type));
      setAvailableDataTypes(typesPresent);

      setData(allData);

      if (allData.length === 0) {
        setAvailableDataPeriod(null);
      } else {
        let minTs = Infinity, maxTs = -Infinity;
        for (const d of allData) {
          if (d.ts < minTs) minTs = d.ts;
          if (d.ts > maxTs) maxTs = d.ts;
        }
        setAvailableDataPeriod({ start: new Date(minTs), end: new Date(maxTs) });
      }
    } catch (err) {
      console.error("Erreur chargement des données:", err);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  // Index des données par (type, tsMinute) pour un accès O(1) lors du rendu
  const dataIndex = useMemo(() => {
    const isProducer = participantType === 'producer';
    let eansToFilter = isProducer && groupEans.length > 0 ? new Set(groupEans) : new Set([participantEan]);

    if (!selectedParticipantsFilter.includes("Tous") && selectedParticipantsFilter.length > 0) {
      const selectedEans = new Set();
      Object.entries(eanToNameRef.current).forEach(([ean, name]) => {
        if (selectedParticipantsFilter.includes(name)) selectedEans.add(ean);
      });
      if (selectedEans.size > 0) eansToFilter = selectedEans;
    }

    // Map<type, Map<bucketTs, volume>>
    const index = new Map();
    for (const d of data) {
      if (!eansToFilter.has(d.ean)) continue;
      if (!index.has(d.type)) index.set(d.type, new Map());
      const typeMap = index.get(d.type);
      const current = typeMap.get(d.ts) || 0;
      typeMap.set(d.ts, current + d.volume);
    }
    return index;
  }, [data, participantEan, participantType, groupEans, selectedParticipantsFilter]);

  const chartData = useMemo(() => {
    if (dataIndex.size === 0) return [];

    let start, end;

    if (granularity === "day") {
      start = startOfDay(selectedDate);
      end = endOfDay(selectedDate);
    } else if (granularity === "week") {
      start = startOfWeek(selectedDate, { weekStartsOn: 1 });
      end = endOfWeek(selectedDate, { weekStartsOn: 1 });
    } else {
      start = startOfYear(selectedDate);
      end = endOfYear(selectedDate);
    }

    const grouped = [];

    if (granularity === "year") {
      for (let month = 0; month < 12; month++) {
        const monthStart = new Date(selectedDate.getFullYear(), month, 1).getTime();
        const monthEnd = new Date(selectedDate.getFullYear(), month + 1, 0, 23, 59, 59, 999).getTime();
        const label = format(new Date(monthStart), "MMM", { locale: getDateLocale() });
        const obj = { name: label };
        for (const type of ALL_TYPES) {
          let sum = 0;
          const typeMap = dataIndex.get(type);
          if (typeMap) {
            for (const [ts, vol] of typeMap) {
              if (ts >= monthStart && ts <= monthEnd) sum += vol;
            }
          }
          obj[type] = sum;
        }
        grouped.push(obj);
      }
    } else {
      const step = granularity === "day" ? 15 : 24 * 60;
      const maxIterations = granularity === "day" ? 96 : 7;
      let current = new Date(start.getTime());
      let iterations = 0;

      while (current.getTime() <= end.getTime() && iterations < maxIterations) {
        const next = addMinutes(current, step);
        const currentTime = current.getTime();
        const nextTime = next.getTime();

        let label;
        try {
          label = granularity === "day"
            ? format(current, "HH:mm")
            : format(current, "EEE dd/MM", { locale: getDateLocale() });
        } catch {
          label = `Point ${iterations}`;
        }

        const obj = { name: label };
        for (const type of ALL_TYPES) {
          let sum = 0;
          const typeMap = dataIndex.get(type);
          if (typeMap) {
            for (const [ts, vol] of typeMap) {
              if (ts >= currentTime && ts < nextTime) sum += vol;
            }
          }
          obj[type] = sum;
        }
        grouped.push(obj);
        current = next;
        iterations++;
      }
    }

    return grouped;
  }, [dataIndex, granularity, selectedDate]);

  const modeChartData = useMemo(() => chartData.map(d => {
    const shared = d[`${modePrefix} Partagée`] || 0;
    const network = d[`${modePrefix} Réseau`] || 0;
    return { name: d.name, shared, network, total: shared + network };
  }), [chartData, modePrefix]);

  const handlePrevPeriod = () => {
    if (granularity === "day") {
      setSelectedDate(new Date(selectedDate.getTime() - 24 * 60 * 60 * 1000));
    } else if (granularity === "week") {
      setSelectedDate(new Date(selectedDate.getTime() - 7 * 24 * 60 * 60 * 1000));
    } else {
      setSelectedDate(new Date(selectedDate.getFullYear() - 1, 0, 1));
    }
  };

  const handleNextPeriod = () => {
    if (granularity === "day") {
      setSelectedDate(new Date(selectedDate.getTime() + 24 * 60 * 60 * 1000));
    } else if (granularity === "week") {
      setSelectedDate(new Date(selectedDate.getTime() + 7 * 24 * 60 * 60 * 1000));
    } else {
      setSelectedDate(new Date(selectedDate.getFullYear() + 1, 0, 1));
    }
  };

  const getPeriodLabel = () => {
    if (granularity === "day") {
      return format(selectedDate, "EEEE d MMMM yyyy", { locale: getDateLocale() });
    } else if (granularity === "week") {
      const s = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const e = endOfWeek(selectedDate, { weekStartsOn: 1 });
      return `${format(s, "d MMM", { locale: getDateLocale() })} - ${format(e, "d MMM yyyy", { locale: getDateLocale() })}`;
    } else {
      return selectedDate.getFullYear().toString();
    }
  };

  const toggleArea = (key) => {
    setVisibleAreas(prev => {
      const other = key === 'total' ? 'shared' : 'total';
      if (prev[key] && !prev[other]) return prev; // au moins une aire visible
      return { ...prev, [key]: !prev[key] };
    });
  };

  const sharedLabel = effectiveMode === 'inj' ? t('memberDashboard.chart.sharedInjection') : t('memberDashboard.chart.sharedConsumption');
  const networkLabel = effectiveMode === 'inj' ? t('memberDashboard.chart.networkInjection') : t('memberDashboard.chart.networkConsumption');
  const totalLabel = effectiveMode === 'inj' ? t('memberEanChart.totalInjection') : t('memberEanChart.totalConsumption');

  const QhTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload;
    const line = (color, name, value, bold) => (
      <div className={`flex justify-between items-center gap-6 py-0.5 ${bold ? 'font-semibold text-[#0C1C2C]' : ''}`}>
        <span className="flex items-center gap-2 text-[#51626E]">
          {color && <span className="w-3 h-3 rounded-[3px]" style={{ background: color }} />}
          {name}
        </span>
        <span className="tabular-nums font-semibold text-[#0C1C2C]">{value.toFixed(2)} kWh</span>
      </div>
    );
    return (
      <div className="bg-white border border-[#C9DFE5] rounded-xl shadow-lg px-4 py-3 text-sm min-w-[230px] font-sans">
        <p className="font-semibold text-[#0C1C2C] mb-2 pb-2 border-b border-[#E3ECEF]">{label}</p>
        {line(QH.sharedStroke, sharedLabel, row.shared)}
        {line(QH.totalStroke, networkLabel, row.network)}
        <div className="border-t border-[#E3ECEF] mt-1.5 pt-1.5">{line(null, totalLabel, row.total, true)}</div>
      </div>
    );
  };

  if (loading) {
    const pct = loadingProgress.total > 0
      ? Math.round((loadingProgress.done / loadingProgress.total) * 100)
      : 0;
    return (
      <div className="bg-white p-5 md:p-6 rounded-2xl border border-[#C9DFE5]">
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FCAE0A]"></div>
          {loadingProgress.total > 0 && (
            <div className="w-full max-w-xs">
              <div className="flex justify-between text-xs text-[#51626E] mb-1">
                <span>{t('memberUi.loadingData')}</span>
                <span>{t('memberUi.files', { done: loadingProgress.done, total: loadingProgress.total })}</span>
              </div>
              <div className="h-2 bg-[#E3ECEF] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#FCAE0A] rounded-full transition-all duration-300"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-5 md:p-6 rounded-2xl border border-[#C9DFE5] font-sans text-[#0C1C2C]">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-sans text-xl leading-tight font-bold text-[#00516F]">{t('memberEanChart.title')}</h2>
        <button
          onClick={() => loadData(true)}
          className="flex items-center gap-2 h-9 px-3 text-sm font-medium text-[#0C1C2C] border border-[#C9DFE5] rounded-[10px] hover:border-[#00516F] transition-colors"
          title={t('memberUi.forceReload')}
        >
          <RefreshCw className="w-4 h-4" />
          {t('memberUi.refresh')}
        </button>
      </div>

      <div className="flex flex-col gap-5">
        {groupEans.length > 1 && participants.length > 1 && (
          <div className="bg-[#F7FAFB] rounded-xl p-4 border border-[#E3ECEF]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-[#00516F]" />
                <span className="font-medium text-[#0C1C2C]">{t('memberEanChart.filterLabel')}</span>
              </div>
              <button
                onClick={() => setSelectedParticipantsFilter(["Tous"])}
                className="text-sm text-[#00516F] hover:text-[#0C1C2C] font-medium"
              >
                {t('memberEanChart.showAll')}
              </button>
            </div>

            {!selectedParticipantsFilter.includes("Tous") && selectedParticipantsFilter.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {selectedParticipantsFilter.map(p => (
                  <span key={p} className="inline-flex items-center gap-1 px-3 py-1 bg-[#EEF5F7] border border-[#C9DFE5] text-[#00516F] rounded-full text-sm font-medium">
                    {p}
                    <button
                      onClick={() => {
                        const newSelection = selectedParticipantsFilter.filter(s => s !== p);
                        setSelectedParticipantsFilter(newSelection.length > 0 ? newSelection : ["Tous"]);
                      }}
                      className="ml-1 hover:bg-[#C9DFE5] rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="relative" ref={filterRef}>
              <button
                onClick={() => setShowParticipantFilter(!showParticipantFilter)}
                className="w-full flex items-center justify-between px-4 h-10 bg-white border border-[#C9DFE5] rounded-[10px] hover:border-[#00516F] transition-colors"
              >
                <span className="text-[#0C1C2C] text-sm">
                  {selectedParticipantsFilter.includes("Tous")
                    ? `${t('memberEanChart.allParticipants')} (${participants.length - 1})`
                    : t('memberUi.selectedParticipants', { count: selectedParticipantsFilter.length })}
                </span>
                <ChevronDown className={`w-5 h-5 text-[#51626E] transition-transform ${showParticipantFilter ? 'rotate-180' : ''}`} />
              </button>

              {showParticipantFilter && (
                <div className="absolute z-20 mt-2 w-full bg-white border border-[#C9DFE5] rounded-xl shadow-xl max-h-64 overflow-y-auto">
                  <div className="p-2 border-b border-[#E3ECEF] sticky top-0 bg-white">
                    <button
                      onClick={() => { setSelectedParticipantsFilter(["Tous"]); setShowParticipantFilter(false); }}
                      className={`w-full text-left px-3 py-2 rounded-md transition-colors ${selectedParticipantsFilter.includes("Tous") ? 'bg-[#EEF5F7] text-[#00516F] font-medium' : 'hover:bg-[#F2F2F3]'}`}
                    >
                      {t('memberEanChart.allParticipants')}
                    </button>
                  </div>
                  <div className="p-2">
                    {participants.filter(p => p !== "Tous").map(p => (
                      <label key={p} className="flex items-center gap-3 px-3 py-2 hover:bg-[#F2F2F3] rounded-md cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedParticipantsFilter.includes(p)}
                          onChange={(e) => {
                            let newSelection;
                            if (e.target.checked) {
                              newSelection = [...selectedParticipantsFilter.filter(s => s !== "Tous"), p];
                            } else {
                              newSelection = selectedParticipantsFilter.filter(s => s !== p);
                            }
                            setSelectedParticipantsFilter(newSelection.length > 0 ? newSelection : ["Tous"]);
                          }}
                          className="w-4 h-4 accent-[#00516F] rounded border-[#C9DFE5]"
                        />
                        <span className="text-sm text-[#0C1C2C]">{p}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            {showModeSwitch && (
              <div className="inline-flex rounded-[10px] border border-[#C9DFE5] overflow-hidden" role="group" aria-label={`${t('memberDashboard.chart.consumption')} / ${t('memberDashboard.chart.injection')}`}>
                {[['conso', t('memberDashboard.chart.consumption')], ['inj', t('memberDashboard.chart.injection')]].map(([m, label]) => (
                  <button
                    key={m}
                    onClick={() => setQhMode(m)}
                    aria-pressed={effectiveMode === m}
                    className={`px-3.5 h-9 text-sm font-semibold transition-colors ${effectiveMode === m ? 'bg-[#0C1C2C] text-white' : 'bg-white text-[#51626E] hover:bg-[#F2F2F3]'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <div className="inline-flex rounded-[10px] border border-[#C9DFE5] overflow-hidden">
              {["day", "week", "year"].map(g => (
                <button
                  key={g}
                  onClick={() => setGranularity(g)}
                  className={`px-3.5 h-9 text-sm font-medium transition-colors ${granularity === g ? "bg-[#00516F] text-white" : "bg-white text-[#51626E] hover:bg-[#F2F2F3]"}`}
                >
                  {t(`memberEanChart.view${g.charAt(0).toUpperCase() + g.slice(1)}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handlePrevPeriod}
              className="h-9 w-9 flex items-center justify-center rounded-[10px] border border-[#C9DFE5] hover:border-[#00516F] transition-colors"
            >
              <svg className="w-4 h-4 text-[#00516F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <input
              type="date"
              value={format(selectedDate, 'yyyy-MM-dd')}
              onChange={(e) => {
                const d = new Date(e.target.value);
                if (!isNaN(d.getTime())) setSelectedDate(d);
              }}
              className="h-9 px-3 border border-[#C9DFE5] rounded-[10px] text-sm focus:outline-none focus:ring-2 focus:ring-[#00516F]/30"
            />

            <span className="text-sm font-semibold text-[#0C1C2C] min-w-[200px] text-center first-letter:uppercase">
              {getPeriodLabel()}
            </span>

            <button
              onClick={handleNextPeriod}
              className="h-9 w-9 flex items-center justify-center rounded-[10px] border border-[#C9DFE5] hover:border-[#00516F] transition-colors"
            >
              <svg className="w-4 h-4 text-[#00516F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {[
            { key: 'total', label: totalLabel, fill: QH.totalFill, stroke: QH.totalStroke },
            { key: 'shared', label: sharedLabel, fill: QH.sharedFill, stroke: QH.sharedStroke },
          ].map(({ key, label, fill, stroke }) => (
            <button
              key={key}
              type="button"
              onClick={() => toggleArea(key)}
              aria-pressed={visibleAreas[key]}
              className={`inline-flex items-center gap-2 h-8 pl-2.5 pr-3 rounded-full border text-[13px] font-medium transition-colors ${visibleAreas[key] ? 'border-[#0C1C2C] text-[#0C1C2C]' : 'border-[#C9DFE5] text-[#51626E]'}`}
            >
              <span className="w-3.5 h-3.5 rounded-[3px] flex-shrink-0" style={{ background: visibleAreas[key] ? fill : 'transparent', border: `1.5px solid ${stroke}` }} />
              {label}
            </button>
          ))}
        </div>

        <div className="w-full h-[420px]">
          {modeChartData.length > 0 ? (
            <>
              <div className="hidden md:block h-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={modeChartData} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                    <CartesianGrid stroke={QH.grid} vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: QH.muted }} tickLine={false} axisLine={{ stroke: QH.border }} minTickGap={16} />
                    <YAxis tick={{ fontSize: 12, fill: QH.muted }} tickLine={false} axisLine={false} width={56} tickFormatter={(v) => `${v}`} />
                    <Tooltip content={<QhTooltip />} cursor={{ stroke: QH.border }} />
                    {visibleAreas.total && (
                      <Area
                        type="monotone"
                        dataKey="total"
                        stroke={QH.totalStroke}
                        strokeWidth={1.75}
                        fill={QH.totalFill}
                        fillOpacity={1}
                        name={totalLabel}
                        isAnimationActive={false}
                      />
                    )}
                    {visibleAreas.shared && (
                      <Area
                        type="monotone"
                        dataKey="shared"
                        stroke={QH.sharedStroke}
                        strokeWidth={1.75}
                        fill={QH.sharedFill}
                        fillOpacity={1}
                        name={sharedLabel}
                        isAnimationActive={false}
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className="md:hidden flex items-center justify-center h-full text-[#51626E]">
                <div className="text-center bg-[#F2F2F3] rounded-xl p-8">
                  <p className="text-lg font-medium text-[#0C1C2C]">{t('memberUi.desktopOnly')}</p>
                  <p className="text-sm mt-2 text-[#51626E]">{t('memberUi.desktopOnlyText')}</p>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-[#51626E]">
              <div className="text-center">
                <p className="text-lg font-medium">{t('memberUi.noData')}</p>
                <p className="text-sm mt-2">{t('memberUi.otherPeriod')}</p>
                {availableDataPeriod && (
                  <div className="mt-4 p-4 bg-[#EEF5F7] border border-[#C9DFE5] rounded-xl inline-block">
                    <p className="text-sm font-medium text-[#00516F] mb-1">{t('memberUi.availablePeriod')}</p>
                    <p className="text-sm text-[#0C1C2C]">
                      {format(availableDataPeriod.start, 'd MMMM yyyy', { locale: getDateLocale() })} - {format(availableDataPeriod.end, 'd MMMM yyyy', { locale: getDateLocale() })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
